// MIDAS V3 — Markdown helpers and component renderers (shared)
//
// Lifted from ChatBotMessageV3.tsx so that both:
//   • ChatBotMessageV3      — final, persisted assistant messages
//   • StreamingMarkdownV3   — progressive token stream prose
// can render with the SAME ReactMarkdown component overrides.
//
// Keeping the two surfaces visually identical is the whole point of the lift
// — without it, the prose would visually "pop" the moment streaming ended
// and the final message rendered with different styling. See the streaming
// parity bug fix that introduced this module.
//
// Behaviour copied VERBATIM (no logic changes) from the previous inline
// implementation in ChatBotMessageV3.tsx:
//   • preprocessMarkdown — normalises bullets and re-stitches lines under
//     bold "headers" the bot emits as plain text instead of markdown.
//   • rewriteListRefsToSentinels + splitSentinelString — preserve <list-ref>
//     tags across markdown blocks (notably pipe-table cells) by collapsing
//     them to a single-token sentinel that survives the markdown parser.
//   • buildMarkdownComponents — V3-token-styled p / strong / h1-3 / a / ul /
//     ol / li / code / pre / table / thead / th / td / hr renderers, with
//     inline `<CiteV3>` substitution at sentinel sites.
//
// 2026-05-28 — MIDAS-LIVE-REF: extended with parallel sentinel pipelines for
//   • <live-ref address="…" value="…">…</live-ref> → <LiveRefV3>
//   • <val>TRUE|FALSE|…</val>                       → <ValV3>
// Mirrors the existing <list-ref> mechanism so the new tags also survive
// pipe-table cells. See LIVE_REF_INTEGRATION_SPEC.md Steps 3 & 4.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens. Plain inline styles only.
import React from "react";

import CiteV3 from "./CiteV3";
import { LiveRefV3, ValV3, type LiveValue } from "./LiveRefV3";

// ═══════════════════════════════════════════════════════════════
//  Markdown pre-processing — verbatim from V2 (logic only)
// ═══════════════════════════════════════════════════════════════

const isBoldHeaderLine = (line: string): boolean =>
  /^\*\*.+\*\*$/.test(line.trim());
const isAlreadyListItem = (line: string): boolean =>
  /^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line);
const isMarkdownHeading = (line: string): boolean =>
  /^#{1,6}\s+/.test(line.trim());

export const preprocessMarkdown = (text: string): string => {
  if (!text) return "";
  let processed = text.split("\\r\\n").join("\n").split("\\n").join("\n");
  processed = processed.replace(/^[•●◦▪▸►]\s*/gm, "- ");
  const lines = processed.split("\n");
  const result: string[] = [];
  let afterBoldHeader = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (isBoldHeaderLine(trimmed)) {
      afterBoldHeader = true;
      result.push(line);
    } else if (afterBoldHeader && trimmed.length > 0) {
      if (isAlreadyListItem(trimmed) || isMarkdownHeading(trimmed)) {
        result.push(line);
      } else {
        result.push(`- ${trimmed}`);
      }
    } else {
      if (trimmed.length === 0) afterBoldHeader = false;
      result.push(line);
    }
  }
  return result.join("\n");
};

// ═══════════════════════════════════════════════════════════════
//  Sentinel transform — identical to V2's approach so list-refs
//  inside pipe-table cells survive markdown parsing. We keep this
//  in v3 instead of importing from v2 because the v2 component is
//  the read-only reference per CLAUDE.md.
// ═══════════════════════════════════════════════════════════════

const SENTINEL_OPEN = "⟦"; // ⟦
const SENTINEL_CLOSE = "⟧"; // ⟧
// ASCII Unit Separator (U+001F). MUST be a real character — `String.split("")`
// degenerates to per-character split, which combined with `decodeURIComponent`
// throws URIError the moment a body byte lands on a `%` (production-blocking
// regression on /midas-v3 if the bot has ever emitted a <live-ref>). Mirrors
// V2's ChatBotMessageV2.tsx SENTINEL_SEP (verified byte-equal). Use the escape
// sequence so the literal stays visible in source even though the rendered
// character is non-printing.
const SENTINEL_SEP = "";
const LIST_REF_TAG_RE = /<list-ref\b([^>]*)>([\s\S]*?)<\/list-ref>/gi;
const LIVE_REF_TAG_RE = /<live-ref\b([^>]*)>([\s\S]*?)<\/live-ref>/gi;
// MIDAS-LIVE-REF follow-up (2026-05-28): the bot also emits a value-less PLC
// address reference — <plc-ref bit="Q 41.3">Q 41.3</plc-ref> — when it
// describes a signal it did NOT read live. Note the attribute is `bit=`
// (not `address=`) and there is NO `value=`. We funnel it into the SAME
// LIVEREF sentinel as <live-ref> (address = bit, value = undefined) so it
// reuses the entire existing decode/split/render path; LiveRefV3's pipColor
// already returns var(--muted) (grey) for a missing value — the correct
// "no live value" state.
const PLC_REF_TAG_RE = /<plc-ref\b([^>]*)>([\s\S]*?)<\/plc-ref>/gi;
const VAL_TAG_RE = /<val\b([^>]*)>([\s\S]*?)<\/val>/gi;
const LIST_DOUBLE_BRACKET_RE = /\[\[list:(\d+)\]\]/gi;

const readAttr = (attrs: string, name: string): string | undefined => {
  const re = new RegExp(
    `\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`,
    "i"
  );
  const m = attrs.match(re);
  if (!m) return undefined;
  return m[1] ?? m[2] ?? m[3];
};

const buildSentinel = (
  page: number,
  listId: string,
  name: string | undefined,
  label: string
): string | null => {
  if (!Number.isFinite(page) || page <= 0) return null;
  const encName = name ? encodeURIComponent(name) : "";
  const encLabel = encodeURIComponent(label);
  return `${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}${page}${SENTINEL_SEP}${encodeURIComponent(
    listId || ""
  )}${SENTINEL_SEP}${encName}${SENTINEL_SEP}${encLabel}${SENTINEL_CLOSE}`;
};

export const rewriteListRefsToSentinels = (text: string): string => {
  if (!text) return "";

  let out = text.replace(LIST_REF_TAG_RE, (full, attrs, rawLabel) => {
    const label = (rawLabel || "").trim();
    const pageStr = readAttr(attrs || "", "page");
    const listId = readAttr(attrs || "", "id") ?? "";
    const name = readAttr(attrs || "", "name");
    const page = pageStr !== undefined ? Number(pageStr) : NaN;
    const sentinel = buildSentinel(page, listId, name, label);
    return sentinel ?? full;
  });

  // Strip surrounding `[…]` brackets that hug a sentinel.
  out = out.replace(
    new RegExp(
      `\\[\\s*(${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\\]`,
      "g"
    ),
    "$1"
  );

  // Strip surrounding backticks that hug a sentinel.
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  // `[[list:NNN]]` shorthand.
  out = out.replace(LIST_DOUBLE_BRACKET_RE, (_full, num) => {
    const page = Number(num);
    const listId = String(num).padStart(3, "0");
    const label = `List ${listId}`;
    const sentinel = buildSentinel(page, listId, undefined, label);
    return sentinel ?? `[[list:${num}]]`;
  });

  return out;
};

// ═══════════════════════════════════════════════════════════════
//  Live-ref + Val sentinel rewrites (MIDAS-LIVE-REF, 2026-05-28)
//
//  Mirror the <list-ref> approach so the new tags also survive
//  ReactMarkdown's parser (including pipe-table cells).
//
//  Sentinel formats:
//    ⟦LIVEREF␟<encAddress>␟<encValue>␟<encLabel>⟧
//    ⟦VAL␟<encText>⟧
//
//  Both attributes / inner text are encodeURIComponent'd so the
//  delimiter character (Unit Separator U+001F) can never appear in
//  a payload field.
// ═══════════════════════════════════════════════════════════════

const buildLiveRefSentinel = (
  address: string,
  value: string | undefined,
  label: string
): string => {
  const encAddress = encodeURIComponent(address);
  const encValue = value !== undefined ? encodeURIComponent(value) : "";
  const encLabel = encodeURIComponent(label);
  return `${SENTINEL_OPEN}LIVEREF${SENTINEL_SEP}${encAddress}${SENTINEL_SEP}${encValue}${SENTINEL_SEP}${encLabel}${SENTINEL_CLOSE}`;
};

const buildValSentinel = (text: string): string => {
  const encText = encodeURIComponent(text);
  return `${SENTINEL_OPEN}VAL${SENTINEL_SEP}${encText}${SENTINEL_CLOSE}`;
};

export const rewriteLiveRefsToSentinels = (text: string): string => {
  if (!text) return "";
  let out = text.replace(LIVE_REF_TAG_RE, (full, attrs, rawLabel) => {
    const address = readAttr(attrs || "", "address");
    if (!address) return full; // malformed — leave verbatim so it's visible
    const value = readAttr(attrs || "", "value");
    const label = ((rawLabel || "") as string).trim() || address;
    return buildLiveRefSentinel(address, value, label);
  });

  // <plc-ref bit="…">…</plc-ref> — value-less PLC reference. Funnelled into
  // the SAME LIVEREF sentinel with value=undefined so it reuses the live-ref
  // decode/render path and renders as a grey-pip chip. Run AFTER <live-ref>
  // so the two tag families don't interfere; they never nest.
  out = out.replace(PLC_REF_TAG_RE, (full, attrs, rawLabel) => {
    const bit = readAttr(attrs || "", "bit");
    if (!bit) return full; // malformed — leave verbatim so it's visible
    const label = ((rawLabel || "") as string).trim() || bit;
    // value omitted → buildLiveRefSentinel emits an empty value field →
    // decodeLiveRefBody maps "" back to undefined → grey "no live value" pip.
    return buildLiveRefSentinel(bit, undefined, label);
  });

  // Strip surrounding backticks that hug a sentinel — same defensive pattern
  // as the list-ref pipeline (the bot occasionally wraps inline values in `…`).
  // Covers both <live-ref>- and <plc-ref>-derived LIVEREF sentinels.
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}LIVEREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  return out;
};

export const rewriteValsToSentinels = (text: string): string => {
  if (!text) return "";
  let out = text.replace(VAL_TAG_RE, (full, _attrs, rawText) => {
    const inner = ((rawText || "") as string).trim();
    if (!inner) return full;
    return buildValSentinel(inner);
  });

  // Strip surrounding backticks that hug a val sentinel.
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}VAL${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  return out;
};

/**
 * Compose all three sentinel rewrites in the order the spec requires:
 *   1. live-refs first  — most likely to be nested inside other prose.
 *   2. val tags second  — they live in the surrounding prose, not inside
 *      live-refs, so order is largely cosmetic; doing it after live-refs
 *      avoids any accidental overlap if the bot ever wraps a val in a
 *      live-ref's inner text.
 *   3. list-refs last   — pre-existing behaviour, untouched.
 *
 * Exposed so callers can chain a single rewrite pass instead of remembering
 * the order. Both ChatBotMessageV3 and any future surface (StreamingMarkdownV3
 * if it ever needs sentinels) should go through this.
 */
export const rewriteAllRefsToSentinels = (text: string): string => {
  if (!text) return "";
  return rewriteListRefsToSentinels(
    rewriteValsToSentinels(rewriteLiveRefsToSentinels(text))
  );
};

const SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}(\\d+)${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

// Live-ref sentinel: ⟦LIVEREF␟<encAddress>␟<encValue>␟<encLabel>⟧
const LIVE_REF_SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}LIVEREF${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

// Val sentinel: ⟦VAL␟<encText>⟧
const VAL_SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}VAL${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

// Combined matcher so a single linear scan can split a text run into
// alternating text / list-ref / live-ref / val segments without re-walking
// the string three times. Note: per-type regexes above are kept for the
// `code` unwrap path which only cares about a single sentinel type at a time.
const ANY_SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}(LISTREF|LIVEREF|VAL)${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

interface SentinelPayload {
  page: number;
  listId: string;
  name?: string;
  label: string;
}

interface LiveRefPayload {
  address: string;
  value: string | undefined;
  label: string;
}

type SentinelSplitItem =
  | { kind: "text"; text: string }
  | { kind: "ref"; payload: SentinelPayload }
  | { kind: "live"; payload: LiveRefPayload }
  | { kind: "val"; payload: { text: string } };

// decodeURIComponent throws URIError on a malformed sequence (e.g. a lone
// `%` not followed by two hex digits). All sentinel payload fields are
// encodeURIComponent'd on the way in, so the round-trip is normally safe —
// but a malformed bot attribute (or a future tag we funnel in) must NOT be
// able to throw and white-screen /midas-v3. Degrade to the raw string so the
// chip still renders (showing the un-decoded address) instead of crashing.
const safeDecode = (s: string): string => {
  if (!s) return "";
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

const decodeListRefBody = (body: string): SentinelPayload => {
  const fields = body.split(SENTINEL_SEP);
  const page = Number(fields[0]);
  const listId = safeDecode(fields[1] || "");
  const rawName = safeDecode(fields[2] || "");
  const label = safeDecode(fields[3] || "") || `List ${listId || page}`;
  return {
    page,
    listId,
    name: rawName || undefined,
    label,
  };
};

const decodeLiveRefBody = (body: string): LiveRefPayload => {
  const fields = body.split(SENTINEL_SEP);
  const address = safeDecode(fields[0] || "");
  const rawValue = safeDecode(fields[1] || "");
  const label = safeDecode(fields[2] || "") || address;
  return {
    address,
    value: rawValue === "" ? undefined : rawValue,
    label,
  };
};

const decodeValBody = (body: string): { text: string } => {
  return { text: safeDecode(body || "") };
};

/**
 * Coerce the raw bot-emitted `value` attribute into the type LiveRefV3
 * accepts. Per spec Step 3 ¶4:
 *   "TRUE" → true, "FALSE" → false, otherwise pass through as string
 *   (don't pre-coerce to number — the chip's pip-color logic accepts
 *   string-with-units like "1.23 bar" per handoff README §Visual spec).
 */
const parseLiveRefValue = (raw: string | undefined): LiveValue => {
  if (raw === undefined) return undefined;
  if (raw === "TRUE") return true;
  if (raw === "FALSE") return false;
  return raw;
};

const splitSentinelString = (s: string): SentinelSplitItem[] => {
  const out: SentinelSplitItem[] = [];
  if (!s) return out;
  let lastIndex = 0;
  ANY_SENTINEL_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = ANY_SENTINEL_RE.exec(s)) !== null) {
    if (m.index > lastIndex) {
      out.push({ kind: "text", text: s.slice(lastIndex, m.index) });
    }
    const tag = m[1];
    const body = m[2] || "";
    if (tag === "LISTREF") {
      out.push({ kind: "ref", payload: decodeListRefBody(body) });
    } else if (tag === "LIVEREF") {
      out.push({ kind: "live", payload: decodeLiveRefBody(body) });
    } else if (tag === "VAL") {
      out.push({ kind: "val", payload: decodeValBody(body) });
    } else {
      // Unknown tag — fall through to text so we never silently drop content.
      out.push({ kind: "text", text: m[0] });
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < s.length) {
    out.push({ kind: "text", text: s.slice(lastIndex) });
  }
  return out;
};

const renderChildrenWithSentinels = (
  children: React.ReactNode,
  resolveActive: (page: number) => boolean
): React.ReactNode => {
  if (children == null) return children;
  if (typeof children === "string") {
    const parts = splitSentinelString(children);
    if (parts.length === 0) return "";
    if (parts.length === 1 && parts[0].kind === "text") return parts[0].text;
    return parts.map((p, i) => {
      if (p.kind === "text") {
        return <React.Fragment key={i}>{p.text}</React.Fragment>;
      }
      if (p.kind === "live") {
        // LiveRefV3 owns its own tooltip + onClick placeholder. anim defaults
        // to "glow" — LOCKED app-wide per LIVE_REF_INTEGRATION_SPEC.md Step 6;
        // do NOT pass an override here. `value` is coerced from the raw
        // attribute via parseLiveRefValue: "TRUE"/"FALSE" → boolean, anything
        // else passes through as a string for the chip's pip-color logic.
        return (
          <LiveRefV3
            key={i}
            address={p.payload.address}
            value={parseLiveRefValue(p.payload.value)}
          />
        );
      }
      if (p.kind === "val") {
        // Companion to LiveRefV3 — TRUE / FALSE / numeric text with matching
        // red/green/accent semantic. Bot doesn't emit <val> reliably yet;
        // parser is in place so it lights up automatically when it does.
        return <ValV3 key={i}>{p.payload.text}</ValV3>;
      }
      // p.kind === "ref" — existing <list-ref> → <CiteV3> path. Unchanged.
      const isActive = resolveActive(p.payload.page);
      return (
        <CiteV3
          key={i}
          page={p.payload.page}
          label={p.payload.label}
          tooltip={p.payload.name}
        >
          {/* Active state — bolder background fill. Plain chip otherwise. */}
          <span
            style={
              isActive
                ? {
                    background: "var(--accent)",
                    color: "var(--card)",
                    padding: "0 4px",
                    borderRadius: 3,
                    margin: "-1px -5px",
                  }
                : undefined
            }
          >
            {p.payload.label}
          </span>
        </CiteV3>
      );
    });
  }
  if (Array.isArray(children)) {
    return children.map((c, i) => (
      <React.Fragment key={i}>
        {renderChildrenWithSentinels(c, resolveActive)}
      </React.Fragment>
    ));
  }
  return children;
};

// ═══════════════════════════════════════════════════════════════
//  v3 markdown renderers — inline-styled with v3 tokens. No Tailwind.
// ═══════════════════════════════════════════════════════════════

export const buildMarkdownComponents = (
  resolveActive: (page: number) => boolean
): object => {
  const renderText = (children: React.ReactNode) =>
    renderChildrenWithSentinels(children, resolveActive);

  return {
    p: ({ children }: any) => (
      <p
        style={{
          margin: "0 0 8px 0",
          fontSize: 14,
          lineHeight: 1.5,
          color: "var(--ink)",
          fontFamily: "var(--font-sans)",
        }}
      >
        {renderText(children)}
      </p>
    ),
    strong: ({ children }: any) => (
      <strong style={{ fontWeight: 600, color: "var(--ink)" }}>
        {renderText(children)}
      </strong>
    ),
    em: ({ children }: any) => (
      <em style={{ fontStyle: "italic" }}>{renderText(children)}</em>
    ),
    h1: ({ children }: any) => (
      <h1
        style={{
          margin: "10px 0 6px 0",
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1.3,
          color: "var(--ink)",
        }}
      >
        {renderText(children)}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2
        style={{
          margin: "10px 0 6px 0",
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1.3,
          color: "var(--ink)",
        }}
      >
        {renderText(children)}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3
        style={{
          margin: "10px 0 6px 0",
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1.3,
          color: "var(--ink)",
        }}
      >
        {renderText(children)}
      </h3>
    ),
    a: ({ href, children }: any) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "var(--accent)", textDecoration: "none" }}
      >
        {renderText(children)}
      </a>
    ),
    ul: ({ children }: any) => (
      <ul style={{ margin: "6px 0", paddingLeft: 18, listStyle: "disc" }}>
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol style={{ margin: "6px 0", paddingLeft: 18, listStyle: "decimal" }}>
        {children}
      </ol>
    ),
    li: ({ children }: any) => (
      <li
        style={{
          margin: "3px 0",
          fontSize: 13,
          lineHeight: 1.5,
          color: "var(--ink)",
        }}
      >
        {renderText(children)}
      </li>
    ),
    code: ({ inline, children, className }: any) => {
      // react-markdown 9 (this project's version) REMOVED the `inline` prop —
      // the `code` renderer is called for BOTH inline and fenced code with
      // `inline === undefined`. Relying on `if (inline)` therefore sent EVERY
      // inline code (e.g. a bot symbol name like `I_DOOR_CLOSE`) down the
      // `<pre>` branch, rendering it as a full-width grey block inside table
      // cells and prose (actual2/actual3 screenshots). Detect inline-vs-block
      // from the real v9 signals instead:
      //   • a fenced block carries a `language-*` className (from the info
      //     string) and/or spans multiple lines;
      //   • inline code has neither.
      // If a future react-markdown restores the `inline` prop, honour it.
      const codeText =
        typeof children === "string"
          ? children
          : Array.isArray(children) &&
            children.length === 1 &&
            typeof children[0] === "string"
          ? children[0]
          : null;
      const hasLanguageClass = /language-/.test(className || "");
      const isMultiline = codeText != null && codeText.includes("\n");
      const isInline =
        inline === true ||
        (inline === undefined && !hasLanguageClass && !isMultiline);

      if (isInline) {
        // If the inline code wraps ONLY a sentinel, unwrap so the chip renders
        // as a button / chip, not as `<code><button/></code>` (same bug v2 fixed
        // for list-refs; mirrored here for live-refs + val tags).
        if (typeof children === "string") {
          const trimmed = children.trim();
          const isAnySentinel =
            (trimmed.startsWith(SENTINEL_OPEN + "LISTREF") ||
              trimmed.startsWith(SENTINEL_OPEN + "LIVEREF") ||
              trimmed.startsWith(SENTINEL_OPEN + "VAL")) &&
            trimmed.endsWith(SENTINEL_CLOSE);
          if (isAnySentinel) {
            const stripped = trimmed
              .replace(SENTINEL_RE, "")
              .replace(LIVE_REF_SENTINEL_RE, "")
              .replace(VAL_SENTINEL_RE, "");
            if (stripped.trim() === "") {
              return <>{renderText(children)}</>;
            }
          }
        }
        return (
          <code
            className="mono"
            style={{
              // Force inline so it can never be painted as a full-width block,
              // even nested in a table cell (the bug this fix targets).
              display: "inline",
              width: "auto",
              whiteSpace: "normal",
              verticalAlign: "baseline",
              fontSize: 12.5,
              color: "var(--ink-2)",
              background: "var(--card-2)",
              padding: "1px 4px",
              borderRadius: 3,
            }}
          >
            {renderText(children)}
          </code>
        );
      }
      return (
        <pre
          className="mono"
          style={{
            margin: "8px 0",
            maxHeight: 240,
            overflow: "auto",
            padding: 12,
            background: "var(--card-2)",
            border: "1px solid var(--border-soft)",
            borderRadius: 8,
            fontSize: 12,
          }}
        >
          <code className={className}>{children}</code>
        </pre>
      );
    },
    table: ({ children }: any) => (
      <div style={{ overflowX: "auto", margin: "10px 0" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
          }}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: any) => (
      <thead style={{ borderBottom: "1px solid var(--border)" }}>
        {children}
      </thead>
    ),
    th: ({ children }: any) => (
      <th
        className="mono"
        style={{
          padding: "8px 10px",
          textAlign: "left",
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--faint)",
        }}
      >
        {renderText(children)}
      </th>
    ),
    td: ({ children }: any) => (
      <td
        style={{
          padding: "8px 10px",
          borderBottom: "1px solid var(--border-soft)",
          verticalAlign: "top",
          color: "var(--ink-2)",
        }}
      >
        {renderText(children)}
      </td>
    ),
    hr: () => (
      <hr
        style={{
          margin: "12px 0",
          border: 0,
          borderTop: "1px dashed var(--border)",
        }}
      />
    ),
  };
};
